<script setup>

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { getSwiper } from '@/utils/swiper-loader'

// Props
const props = defineProps({
  logoGardenImages: {
    type: Array,
    default: () => []
  }
})

const images = computed(() => {
  const originalImages = props.logoGardenImages || []
  
  // Duplicate images if we have less than 20 to ensure smooth infinite scrolling
  if (originalImages.length > 0 && originalImages.length < 20) {
    const duplicated = []
    while (duplicated.length < 20) {
      duplicated.push(...originalImages)
    }
    return duplicated
  }
  
  return originalImages
})

const swiperRef = ref(null)
const isSwiperInitialized = ref(false)
let observerInstance = null
let swiperInstance = null
let initPromise = null

// Methods
const initSwiper = async () => {
  if (!swiperRef.value || isSwiperInitialized.value) return
  if (initPromise) return initPromise

  initPromise = (async () => {
    const { Swiper, modules } = await getSwiper(['Autoplay', 'FreeMode'])

    if (!swiperRef.value || isSwiperInitialized.value) return

    swiperInstance = new Swiper(swiperRef.value, {
      slidesPerView: 'auto',
      loop: true,
      spaceBetween: 0,
      freeMode: true,
      freeModeMomentum: false,
      freeModeMomentumBounce: false,
      allowTouchMove: false,
      speed: 5000,
      autoplay: {
        delay: 0,
        disableOnInteraction: false,
      },
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      modules
    })

    isSwiperInitialized.value = true
  })()

  return initPromise
}

// Lifecycle
onMounted(() => {
  if (!swiperRef.value) return
  
  observerInstance = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        initSwiper()
        // Disconnect after initialization to avoid multiple calls
        if (observerInstance) {
          observerInstance.disconnect()
        }
      }
    })
  }, {
    threshold: 0.1
  })
  
  observerInstance.observe(swiperRef.value)

  const handleFirstInteraction = () => {
    initSwiper()
    swiperRef.value?.removeEventListener('pointerdown', handleFirstInteraction, true)
    swiperRef.value?.removeEventListener('touchstart', handleFirstInteraction, true)
  }
  swiperRef.value.addEventListener('pointerdown', handleFirstInteraction, true)
  swiperRef.value.addEventListener('touchstart', handleFirstInteraction, true)

  const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1500))
  idle(() => {
    if (isSwiperInitialized.value) return
    if (!swiperRef.value) return
    const rect = swiperRef.value.getBoundingClientRect()
    const vh = window.innerHeight || 0
    const inViewport = rect.top < vh && rect.bottom > 0
    if (!inViewport) return
    initSwiper()
  })
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<div v-if="logoGardenImages?.length"
  class="swiper" 
  ref="swiperRef"
  role="region"
  aria-label="Logo carousel"
  aria-roledescription="carousel"
  aria-live="off">
  <div class="swiper-wrapper" role="list">
    <div class="swiper-slide"
        v-for="(image, index) in images"
        :key="`${image.id}-${index}`"
        role="listitem"
        aria-hidden="true">
      <GlobalsImage
        :image="image"
        :alt="image.alt || image.title || 'Company logo'"
        :quality="70"
      />
    </div>
  </div>
</div>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.swiper {
  overflow: hidden;
  pointer-events: none;
}

.swiper-wrapper {
  display: flex;
  box-sizing: content-box;
  width: 100%;
  height: 100%;
  position: relative;
  transition-property: transform;
  transition-timing-function: linear !important;
  z-index: 1;
  pointer-events: none;
}

.swiper-slide {
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto !important;
  flex-shrink: 0;
  overflow: hidden;
}

.swiper :deep(img) {
  height: 27px;
  max-width: 146px;
  object-fit: contain;
  padding: 0 11px;
}

@media (min-width: 768px) {
  .swiper :deep(img) {
    height: 57px;
  }
}

</style>